export const KYC_BACKOFF_MS = [1000, 2000, 4000] as const;

type RetryWait = (delayMs: number, signal?: AbortSignal) => Promise<void>;

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted", "AbortError");
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw abortReason(signal);
  }
}

export async function waitForBackoff(delayMs: number, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      clearTimeout(timeout);
      if (signal) {
        reject(abortReason(signal));
      }
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function runWithTransientRetry<Result>(
  attempt: () => Promise<Result>,
  shouldRetry: (result: Result) => boolean,
  wait: RetryWait = waitForBackoff,
  signal?: AbortSignal,
): Promise<Result> {
  throwIfAborted(signal);
  let result = await attempt();
  throwIfAborted(signal);

  for (const delayMs of KYC_BACKOFF_MS) {
    if (!shouldRetry(result)) {
      return result;
    }
    await wait(delayMs, signal);
    throwIfAborted(signal);
    result = await attempt();
    throwIfAborted(signal);
  }

  return result;
}
